"use client";

import {
	createContext,
	useContext,
	useEffect,
	useState,
	useCallback,
} from "react";
import useContactFormParentFields, {
	UseParentFields,
} from "@/app/(withHeaderAndFooter)/(sellPages)/contact-us/components/useContactFormParentFields";
import useContactFormSelectedCampuses, {
	UseContactFormSelectedCampuses,
} from "@/app/(withHeaderAndFooter)/(sellPages)/contact-us/components/useContactFormSelectedCampuses";
import useContactFormStudentFields, {
	UseContactFormStudentFields,
} from "@/app/(withHeaderAndFooter)/(sellPages)/contact-us/components/useContactFormStudentFields";
import {FCWithChildren} from "@/app/types/reactTypes";
import {StudentsByApp} from "@/app/types/Student";
import fetchPlatformUserChildrenByApp from "@/lib/legacyApi/fetchPlatformUserChildrenByApp";
import {sendGTMEvent} from "@next/third-parties/google";

export interface ContactFormContextValues {
	contactFormUserInfo: {
		isLoadingStudents: boolean;
		studentsByApp?: StudentsByApp;
	};
	refreshUserInfo?: () => void;
	parentFieldControls: UseParentFields;
	studentFieldControls: UseContactFormStudentFields;
	selectedCampusControls: UseContactFormSelectedCampuses;
	optInControls: {
		isOptedIntoEmails: boolean;
		setIsOptedIntoEmails: (value: boolean) => void;
	};
}

const ContactFormContext = createContext<ContactFormContextValues | null>(null);

export const ContactFormProvider: FCWithChildren = ({children}) => {
	const [isLoadingStudents, setIsLoadingStudents] = useState<boolean>(true);
	const [studentsByApp, setStudentsByApp] = useState<StudentsByApp>();
	const refreshUserInfo = useCallback(() => {
		fetchPlatformUserChildrenByApp().then((students) => {
			setStudentsByApp(students);
			setIsLoadingStudents(false);
		});
	}, []);
	useEffect(() => {
		refreshUserInfo();
	}, [refreshUserInfo]);
	const parentFieldControls = useContactFormParentFields();
	const studentFieldControls = useContactFormStudentFields();
	const selectedCampusControls = useContactFormSelectedCampuses();
	const [isOptedIntoEmails, _setIsOptedIntoEmails] = useState<boolean>(true);
	const setIsOptedIntoEmails = (value: boolean) => {
		sendGTMEvent({
			is_opted_into_emails: value,
		});
		_setIsOptedIntoEmails(value);
	};

	return (
		<ContactFormContext.Provider
			value={{
				contactFormUserInfo: {
					isLoadingStudents,
					studentsByApp,
				},
				refreshUserInfo,
				parentFieldControls,
				studentFieldControls,
				selectedCampusControls,
				optInControls: {
					isOptedIntoEmails,
					setIsOptedIntoEmails,
				},
			}}
		>
			{children}
		</ContactFormContext.Provider>
	);
};

export const useContactFormContext = () => {
	return useContext(ContactFormContext);
};
